import { TranslationKey } from '../../../../types/translation/TranslationKey';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CompanyContext } from '../../../../context/CompanyContext';
import SettingsIcon from '../../../../VentoryUI/icons/Settings/SettingsIcon';
import { t } from '../../../../types/translation/Translator';
import { useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon } from '../../../../VentoryUI/icons/Home/HomeIcon';
import StockIcon from '../../../../VentoryUI/icons/Stock/StockIcon';
import PackageSearchIcon from '../../../../VentoryUI/icons/Package/PackageSearchIcon';
import { ListIcon } from '../../../../VentoryUI/icons/List/ListIcon';
import BoxedCheckIcon from '../../../../VentoryUI/icons/BoxedCheck/BoxedCheckIcon';
import BellIcon from '../../../../VentoryUI/icons/Bell/BellIcon';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';
import Icon, { IconType } from '../../../icons/Icon';
import MainSidebarItem from './MainSidebarItem';
import { Sidebar, SidebarItem } from '..';
import { Routing } from '../../../../util/routes.util';
import { StockLocationRoleAssignmentContext } from '../../../../context/StockLocationRoleAssignmentContext';
import { UserContext } from '../../../../context/UserContext';
import { StockLocationRole } from '../../../../types/stock_location_role_assignment';
import { ReactComponent as VentoryFavicon } from './VentoryFavicon.svg';
import { CompanyConnectionContext } from '../../../../context/CompanyConnectionContext';
import { SuperUserContext } from '../../../../context/SuperUserContext';
import { CompanyConnectionState } from '../../../../types/companyConnection/companyConnection';

interface MainSidebarProps {}

export function MainSidebar({}: MainSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const { superUser } = useContext(SuperUserContext);
  const { currentUser } = useContext(UserContext);
  const { currentCompany } = useContext(CompanyContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { stockLocationRoles } = useContext(StockLocationRoleAssignmentContext);
  const { companyConnections } = useContext(CompanyConnectionContext);

  const [open, _setOpen] = useState<boolean>(JSON.parse(localStorage.getItem('sidebarOpen') || 'true') ?? true);
  const setOpen = (open: boolean) => {
    _setOpen(open);
    localStorage.setItem('sidebarOpen', JSON.stringify(open));
  };

  const hasCompanyConnections = Array.from(companyConnections.values()).some(cc => cc.parentId === currentCompany.id);
  const pendingConnections = Array.from(companyConnections.values()).some(
    connection => connection.state === CompanyConnectionState.pending && connection.companyId === currentCompany.id,
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const list = useMemo(() => {
    const isStockLocationManager = !![...stockLocationRoles.values()]
      .flat()
      .find(slr => slr.userId === currentUser?.id && slr.role === StockLocationRole.STOCK_LOCATION_MANAGER);

    return [
      {
        index: 0,
        text: t(TranslationKey.dashboard).singular.label,
        path: Routing.dashboard(),
        identifier: '/dashboard',
        icon: <HomeIcon />,
        shown: true,
      },
      {
        index: 1,
        text: t(TranslationKey.stock).singular.label,
        path: Routing.stock.products.overview(),
        identifier: '/stock',
        icon: <StockIcon />,
        shown: true,
      },
      {
        index: 2,
        text: t(TranslationKey.referenceData).singular.label,
        path: Routing.productMasterData.overview(),
        identifier: '/reference_data',
        icon: <PackageSearchIcon />,
        shown: true,
      },
      {
        index: 3,
        text: t(TranslationKey.order).plural.label,
        path: Routing.operations.orders.overview(),
        identifier: '/operations',
        icon: <ListIcon height={20} width={20} />,
        shown: currentCompany.settings.featureToggles.orders.orders,
      },
      {
        index: 4,
        text: t(TranslationKey.assetManagement).singular.label,
        path: Routing.assets.info(),
        identifier: '/assets/',
        icon: <Icon type={IconType.wallet1} size={'size-5'} stroke='stroke-current' />,
        shown: currentCompany.settings.featureToggles.assets.assets && isAdmin,
      },
      {
        index: 5,
        text: t(TranslationKey.myAssets).singular.upper,
        path: Routing.assets.personal.info(),
        identifier: '/assets/personal',
        icon: <Icon type={IconType.backpack} size={'size-5'} stroke='stroke-current' />,
        shown:
          currentCompany.settings.featureToggles.assets.assets &&
          open &&
          (!isAdmin || location.pathname.startsWith('/assets/')),
        indent: isAdmin ? 1 : 0,
      },
      {
        index: 6,
        text: t(TranslationKey.task).plural.label,
        path: Routing.tasks.overview(),
        identifier: '/tasks',
        icon: <BoxedCheckIcon />,
        shown: currentCompany.settings.featureToggles.tasks.tasks,
      },
      {
        index: 7,
        text: t(TranslationKey.alert).plural.label,
        path: Routing.alerts.overview(),
        identifier: '/alerts',
        icon: <BellIcon />,
        shown: currentCompany.settings.featureToggles.alerts.alerts,
      },
      {
        index: 8,
        text: t(TranslationKey.settings).singular.label,
        path: Routing.settings.display.details(),
        identifier: '/settings',
        icon: <SettingsIcon width={20} height={20} />,
        shown: isAdmin || isStockLocationManager,
      },
      {
        index: 9,
        text: t(TranslationKey.partnerPortal).singular.label,
        path: currentCompany.partner ? Routing.partner.overview() : Routing.partner.pending(),
        identifier: '/partner',
        icon: <Icon type={IconType.dataFlow4} size={'size-5'} stroke='stroke-current' />,
        shown: isAdmin && (hasCompanyConnections || pendingConnections || currentCompany.partner),
      },
    ].filter(item => item.shown);
  }, [currentCompany, location, open]);

  useEffect(() => {
    const identifierIndexMap = new Map(list.map(i => [i.identifier, i.index]));

    let bestMatch: string = '';
    let bestMatchCount: number = 0;

    for (const [identifier, index] of identifierIndexMap) {
      let matchLength = 0;

      for (let i = 0; i < identifier.length && i < location.pathname.length; i++) {
        if (identifier[i] !== location.pathname[i]) break;
        matchLength++;
      }

      if (matchLength > bestMatchCount) {
        bestMatch = identifier;
        bestMatchCount = matchLength;
      }
    }

    setSelectedIndex(identifierIndexMap.get(bestMatch) || 0);
  }, [location, open]);

  return (
    <Sidebar
      width={open ? 240 : 64}
      first={
        <>
          {open ? (
            <button
              onClick={() => setOpen(false)}
              className='absolute right-[-12px] top-[12px] flex h-6 w-6 items-center justify-center rounded-full border border-gray-default bg-white p-1'
            >
              <Icon type={IconType.chevronLeft} stroke={'stroke-gray-700'} size={'h-4 w-4'} />
            </button>
          ) : null}
          <div className='grid h-[48px] place-items-center'>
            {!open ? (
              <button
                onClick={() => setOpen(true)}
                className='flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-white p-1'
              >
                <Icon type={IconType.chevronRight} stroke={'stroke-gray-700'} size={'h-4 w-4'} />
              </button>
            ) : null}
          </div>
        </>
      }
      items={list.map(item => {
        if (item.identifier === '/assets/' && !open) {
          return <AssetManagementCollapsedItem />;
        }

        return (
          <MainSidebarItem
            key={item.identifier}
            open={open}
            selected={item.index === selectedIndex}
            icon={item.icon}
            identifier={item.identifier}
            title={item.text}
            onClick={() => navigate(item.path)}
            indent={item.indent}
          />
        );
      })}
      last={
        <>
          {open && (
            <div className='mx-4 mb-6 mt-2 flex items-center gap-2 px-3'>
              <VentoryFavicon />
              <p className='text-xs font-medium text-caption'>Powered by Ventory</p>
            </div>
          )}
        </>
      }
    />
  );
}

// This is a temporary component until we switch to a collapsable system
function AssetManagementCollapsedItem() {
  const navigate = useNavigate();
  const location = useLocation();

  const ref = useRef<HTMLDivElement>(null);

  const [hovered, setHovered] = useState(false);
  const [rect, setRect] = useState({ left: 0, top: 0, width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
  }, []);

  return (
    <SidebarItem
      open={false}
      identifier='/assets/'
      onClick={() => navigate(Routing.assets.info())}
      selected={location.pathname.startsWith('/assets/')}
      title={t(TranslationKey.asset).plural.upper}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => setHovered(false)}
      className={'mx-2 justify-center'}
    >
      <div className='relative flex justify-center gap-2' ref={ref}>
        <Icon type={IconType.wallet1} size={'size-5'} stroke='stroke-current' />
        {hovered && (
          <div
            className='z-40 grid -translate-y-[45%] rounded-2 border bg-white py-1 shadow-default'
            style={{ position: 'fixed', left: rect.left + rect.width + 12, top: rect.top }}
          >
            <SidebarItem
              open
              identifier='/assets/'
              onClick={event => {
                event.stopPropagation();
                navigate(Routing.assets.info());
              }}
              selected={location.pathname.startsWith('/assets/') && !location.pathname.includes('personal')}
              title={t(TranslationKey.assetManagement).singular.upper}
            >
              {t(TranslationKey.assetManagement).singular.upper}
            </SidebarItem>
            <SidebarItem
              open
              identifier='/assets/personal'
              onClick={event => {
                event?.stopPropagation();
                navigate(Routing.assets.personal.info());
              }}
              selected={location.pathname.startsWith('/assets/personal')}
              title={t(TranslationKey.myAssets).singular.upper}
            >
              {t(TranslationKey.myAssets).singular.upper}
            </SidebarItem>
          </div>
        )}
      </div>
    </SidebarItem>
  );
}
